/**
 * Append-only audit ledger — the `auditLogs` table introduced in database
 * version 6.
 *
 * One thing is written here that is written nowhere else: a manager assigning
 * a soldier or a vehicle over a warning ("שבץ בכל זאת"), with the reason they
 * chose, their user id and the moment. The team carries the same override in
 * its own metadata for the UI to render, but the team can be re-saved — this
 * ledger cannot be rewritten, which is what makes it the record.
 *
 * Nothing in this module edits or deletes a row; `resetAuditData()` exists
 * only for the same "reset local data" action that wipes the other tables.
 */
import { db } from './index';
import { assertPermission } from '../core/permissions';
import { describeActorRole } from '../core/access-levels';
import { formatAuditClock } from '../core/audit-stamp';
import { HOME_COMPANY, isHomeCompany } from '../core/vehicle-location';
import { describeOverrideReason } from '../types/assignment';
import type { AssignmentOverride } from '../types/assignment';
import { AUDIT_ACTION_LABELS, CHANGE_HISTORY_ACTIONS, VEHICLE_TRANSFER_ACTIONS } from '../types/audit';
import type {
  AuditAction,
  AuditEntityType,
  AuditLogDraft,
  AuditLogEntry,
  FieldChange,
  UserAdminAction,
} from '../types/audit';
import type { User } from '../types/user';

const newAuditId = (): string => `log-${crypto.randomUUID()}`;

// ── Who acted ────────────────────────────────────────────────────────

/** The identity fields every row carries about the person who wrote it. */
export type ActorStamp = Pick<
  AuditLogEntry,
  'actorId' | 'actorName' | 'actorRole' | 'actorRoleLabel'
>;

/**
 * Reads the actor's job off the roster, so the ledger can name them the way the
 * unit does — "אשר אמר (סמ"פ)" rather than "אשר אמר (מנהל עבודה)".
 *
 * An account with no soldier record behind it simply has no job, and
 * {@link describeActorRole} falls back to its access level. A lookup that fails
 * is not an error worth losing an audit row over: the row is still written, just
 * named by the level.
 */
async function soldierRoleOf(userId: string): Promise<string | null> {
  try {
    const soldier = await db.soldiers.where('userId').equals(userId).first();
    return soldier?.role ?? null;
  } catch {
    return null;
  }
}

/**
 * Freezes who the actor was at the moment they acted — id, name, access role
 * and the job the unit reads them by.
 *
 * The values are copied onto the row rather than joined at read time, which is
 * what makes the ledger a record: renaming an account tomorrow must not rewrite
 * what it did yesterday.
 */
export async function stampActor(actor: User): Promise<ActorStamp> {
  const soldierRole = await soldierRoleOf(actor.id);
  return {
    actorId: actor.id,
    actorName: actor.fullName,
    actorRole: actor.role,
    actorRoleLabel: describeActorRole(actor, soldierRole),
  };
}

// ── Writes ───────────────────────────────────────────────────────────

/**
 * Appends one row. The caller is the guarded action — this is the ledger's
 * pen, not a permission of its own.
 */
export async function recordAuditLog(draft: AuditLogDraft): Promise<AuditLogEntry> {
  const entry: AuditLogEntry = {
    ...draft,
    id: draft.id ?? newAuditId(),
    createdAt: draft.createdAt ?? new Date().toISOString(),
  };
  await db.auditLogs.put(entry);
  return entry;
}

/**
 * Appends one row *about* an actor — the same pen as {@link recordAuditLog},
 * with `actorId` and the three identity fields filled in from the account
 * rather than left to the caller to remember.
 */
export async function recordActorEvent(
  actor: User,
  draft: Omit<AuditLogDraft, 'actorId'>,
): Promise<AuditLogEntry> {
  return recordAuditLog({ ...draft, ...(await stampActor(actor)) });
}

/**
 * Files one "שבץ בכל זאת" decision: the warning that was raised, the reason
 * given, the manager and the timestamp — against the team it was taken on and
 * the task that team rides for.
 */
export async function recordAssignmentOverride(
  actor: User,
  taskId: string,
  teamId: string,
  override: AssignmentOverride,
): Promise<AuditLogEntry> {
  assertPermission(actor, 'ASSIGN_TASK');
  const subject = override.subjectType === 'vehicle' ? 'רכב' : 'חייל';
  return recordAuditLog({
    ...(await stampActor(actor)),
    action: 'ASSIGNMENT_OVERRIDE',
    entityType: 'taskTeam',
    entityId: teamId,
    // The override names its own manager; the stamp above supplies the name and
    // the job that go with them.
    actorId: override.managerId || actor.id,
    taskId,
    summary: `שיבוץ ${subject} בכל זאת — ${override.details} · סיבה: ${describeOverrideReason(override)}`,
    override,
    metadata: {
      conflictType: override.conflictType,
      severity: override.severity,
      subjectType: override.subjectType,
      subjectId: override.subjectId,
      reason: override.reason,
    },
    createdAt: override.createdAt,
  });
}

/**
 * Files one field-execution or closure event against the task it belongs to —
 * a receipt confirmed, an update sent from the field, a soldier's own finish or
 * a manager's closure. The action itself is guarded by its caller in
 * `src/db/task-store.ts`; this is the ledger's pen, exactly as
 * {@link recordAuditLog} is.
 */
export async function recordTaskEvent(
  actor: User,
  action: AuditAction,
  taskId: string,
  summary: string,
  metadata?: Record<string, unknown>,
): Promise<AuditLogEntry> {
  return recordActorEvent(actor, {
    action,
    entityType: 'task',
    entityId: taskId,
    taskId,
    summary,
    metadata,
  });
}

/**
 * Files one vehicle transfer — the loan history "כרטיס רכב" reads back.
 *
 * `from` and `to` are holding companies exactly as `vehicle-location.ts`
 * writes them, and the same module decides which of the two names means "with
 * us": a move to שינוע is a return, anything else is a loan. The transfer
 * itself is guarded by `MANAGE_VEHICLES` in `src/db/fleet-store.ts` — this is
 * the ledger's pen, as {@link recordAuditLog} is.
 */
export async function recordVehicleTransfer(
  actor: User,
  vehicleId: string,
  from: string,
  to: string,
  vehicleLabel: string,
): Promise<AuditLogEntry> {
  const returned = isHomeCompany(to);
  const origin = isHomeCompany(from) ? HOME_COMPANY : from;
  const holder = returned ? HOME_COMPANY : to;
  return recordActorEvent(actor, {
    action: returned ? 'VEHICLE_RETURNED' : 'VEHICLE_LOANED',
    entityType: 'vehicle',
    entityId: vehicleId,
    changes: [{ field: 'assignedCompany', label: 'מחזיקה', from: origin, to: holder }],
    summary: returned
      ? `${vehicleLabel} — הוחזר לשינוע מפלוגת ${origin}`
      : `${vehicleLabel} — הושאל לפלוגת ${holder}`,
    metadata: { from: origin, to: holder },
  });
}

/**
 * Files one hand-made change to a vehicle or a soldier — the rows
 * "היסטוריית שינויים" is read off.
 *
 * The caller has already decided *what* moved (`changes`), because only it knows
 * how to spell its own enums in Hebrew; this function decides how the line reads
 * and who signed it. A call carrying no changes writes nothing at all: an edit
 * that saved the same values back is not a line of history, and a ledger padded
 * with them stops being worth reading.
 *
 * The action itself is guarded by `MANAGE_VEHICLES` / `MANAGE_SOLDIERS` in
 * `src/db/fleet-store.ts` — this is the ledger's pen, as
 * {@link recordAuditLog} is.
 */
export async function recordFleetChange(
  actor: User,
  entityType: Extract<AuditEntityType, 'vehicle' | 'soldier'>,
  entityId: string,
  label: string,
  changes: readonly FieldChange[],
): Promise<AuditLogEntry | null> {
  if (changes.length === 0) return null;
  const statusFields = entityType === 'vehicle' ? VEHICLE_STATUS_FIELDS : SOLDIER_STATUS_FIELDS;
  /** A status move is its own kind of row — it is what the roster is read by. */
  const isStatus = changes.some((change) => statusFields.includes(change.field));
  const action: AuditAction = isStatus
    ? entityType === 'vehicle'
      ? 'VEHICLE_STATUS_CHANGED'
      : 'SOLDIER_STATUS_CHANGED'
    : entityType === 'vehicle'
      ? 'VEHICLE_UPDATED'
      : 'SOLDIER_UPDATED';

  return recordActorEvent(actor, {
    action,
    entityType,
    entityId,
    changes,
    summary: `${label} — ${changes.map(describeFieldChange).join(' · ')}`,
  });
}

/** The fields whose movement makes a row a status change rather than an edit. */
export const VEHICLE_STATUS_FIELDS: readonly string[] = ['status', 'assignedCompany'];
export const SOLDIER_STATUS_FIELDS: readonly string[] = ['availability', 'temporaryStatus'];

/** `מצב תפעולי: זמין ← בטיפול` — one field, in the ledger's own words. */
export const describeFieldChange = (change: FieldChange): string =>
  `${change.label}: ${change.from || '—'} ← ${change.to || '—'}`;

/**
 * Files one action the user-directory screen took against an account — an
 * account opened, a role moved, access switched off, a code reset.
 *
 * Guarded by `MANAGE_USERS` / `RESET_USER_PIN` in `src/db/auth-store.ts`, which
 * is what actually protects the directory; this is the pen.
 */
export async function recordUserAdminEvent(
  actor: User,
  action: UserAdminAction,
  subject: Pick<User, 'id' | 'fullName'>,
  summary: string,
  changes: readonly FieldChange[] = [],
): Promise<AuditLogEntry> {
  return recordActorEvent(actor, {
    action,
    entityType: 'user',
    entityId: subject.id,
    changes,
    summary,
    metadata: { subjectName: subject.fullName },
  });
}

// ── Reads ────────────────────────────────────────────────────────────

/** Newest first — the order a log view reads in. */
const newestFirst = (entries: AuditLogEntry[]): AuditLogEntry[] =>
  [...entries].sort((a, b) => b.createdAt.localeCompare(a.createdAt));

export interface AuditLogFilter {
  action?: AuditAction;
  entityType?: AuditEntityType;
  entityId?: string;
  actorId?: string;
  taskId?: string;
}

/** The whole ledger, or the slice matching a filter. */
export async function listAuditLogs(filter: AuditLogFilter = {}): Promise<AuditLogEntry[]> {
  const rows = await db.auditLogs.toArray();
  return newestFirst(
    rows.filter(
      (row) =>
        (!filter.action || row.action === filter.action) &&
        (!filter.entityType || row.entityType === filter.entityType) &&
        (!filter.entityId || row.entityId === filter.entityId) &&
        (!filter.actorId || row.actorId === filter.actorId) &&
        (!filter.taskId || row.taskId === filter.taskId),
    ),
  );
}

/** Everything ever recorded against one record. */
export async function getEntityAuditLogs(
  entityType: AuditEntityType,
  entityId: string,
): Promise<AuditLogEntry[]> {
  if (!entityId) return [];
  return newestFirst(await db.auditLogs.where('[entityType+entityId]').equals([entityType, entityId]).toArray());
}

/**
 * The loan history of one vehicle, newest first — every השאלה and every החזרה
 * ever filed against it, and nothing else the ledger happens to hold on it.
 */
export async function getVehicleTransferLogs(vehicleId: string): Promise<AuditLogEntry[]> {
  const transfers = new Set<AuditAction>(VEHICLE_TRANSFER_ACTIONS);
  const rows = await getEntityAuditLogs('vehicle', vehicleId);
  return rows.filter((row) => transfers.has(row.action));
}

/** Every override filed for one task's convoy. */
export async function getTaskOverrideLogs(taskId: string): Promise<AuditLogEntry[]> {
  return listAuditLogs({ taskId, action: 'ASSIGNMENT_OVERRIDE' });
}

/**
 * "היסטוריית שינויים" for one vehicle or soldier, newest first — every status
 * move, edit and handover somebody made to it by hand, and nothing else the
 * ledger happens to hold against it.
 *
 * `limit` is what the detail card asks for: the last few changes are the ones
 * anybody reads, and the section stays short enough to sit inside a modal.
 */
export async function getChangeHistory(
  entityType: AuditEntityType,
  entityId: string,
  limit?: number,
): Promise<AuditLogEntry[]> {
  const tracked = new Set<AuditAction>(CHANGE_HISTORY_ACTIONS);
  const rows = (await getEntityAuditLogs(entityType, entityId)).filter((row) =>
    tracked.has(row.action),
  );
  return limit === undefined ? rows : rows.slice(0, limit);
}

/**
 * The time a change is read at: `16:05` for something that happened today, and
 * `12/06 16:05` for anything older.
 *
 * The rule itself lives in `src/core/audit-stamp.ts`, because the per-record
 * indicator ("עודכן ע״י אשר אמר · 16:05") reads a clock too and the two must
 * never drift apart. This is the ledger's name for it.
 */
export const formatChangeStamp = formatAuditClock;

/**
 * Who made a change, and when — `שונה ע״י אשר אמר (סמ"פ) · 16:05`.
 *
 * Read off the row's own frozen identity fields, never off the directory, so a
 * change keeps the name and the job the person held when they made it. A row
 * written before those fields existed says only when it happened, which is still
 * true and still worth showing.
 */
export function describeChangeAuthor(entry: AuditLogEntry, now: number = Date.now()): string {
  const at = formatChangeStamp(entry.createdAt, now);
  if (!entry.actorName) return at;
  const role = entry.actorRoleLabel ? ` (${entry.actorRoleLabel})` : '';
  return `שונה ע״י ${entry.actorName}${role} · ${at}`;
}

/** One line per row, for a plain-text log view. */
export function describeAuditLog(entry: AuditLogEntry): string {
  return `${AUDIT_ACTION_LABELS[entry.action]} — ${entry.summary}`;
}

// ── Maintenance ──────────────────────────────────────────────────────

/** Wipes the ledger — part of the "reset local data" action only. */
export async function resetAuditData(): Promise<void> {
  await db.auditLogs.clear();
}
